/* HERO ANIMATIONS (GSAP) */

export function initHeroAnimations() {
  // Parallax hero manages its own Three.js animation loop
  if (document.querySelector('.parallax-hero')) return;

  const homepageHero  = document.querySelector('.hero--home');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (homepageHero) {
    // Hide text upfront — revealed by the nav-path trigger
    gsap.set([
      '.hero--home .hero-title .word',
      '.hero--home .hero-subtitle',
      '.hero--home .hero-cta-wrapper',
    ], { opacity: 0 });
    gsap.set(['.hero--home .hero-subtitle', '.hero--home .hero-cta-wrapper'], { y: 16 });

    const canvas = document.getElementById('hero-canvas');
    if (canvas) initHeroParticles(canvas);

    if (reducedMotion) {
      gsap.to('.hero--home .hero-title .word', { opacity: 1, stagger: 0.12, duration: 0.5, ease: 'power2.out' });
      gsap.to(['.hero--home .hero-subtitle', '.hero--home .hero-cta-wrapper'],
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.15, delay: 0.6, ease: 'power2.out' });
    } else {
      initSouthernCross();
      initTopoLines();
      initNavPath();
    }
  } else {
    // All other page heroes — existing slide-up reveal
    const tl    = gsap.timeline({ defaults: { ease: 'power3.out' } });
    const words = document.querySelectorAll('.hero-title .word span');
    if (words.length) {
      tl.from(words, { y: '110%', duration: 1.2, stagger: 0.15, delay: 0.2 })
        .from('.hero-subtitle', { y: 20, opacity: 0, duration: 1 }, '-=0.8');
    } else {
      tl.from('.hero-title',    { y: 40, opacity: 0, duration: 1.2, delay: 0.2 })
        .from('.hero-subtitle', { y: 20, opacity: 0, duration: 1 }, '-=0.4');
    }
    tl.from('.hero-cta-wrapper', { scale: 0.8, opacity: 0, duration: 0.8 }, '-=0.6');
  }
}


/* ── Particle constellation background ── */
export function initHeroParticles(canvas) {
  const ctx    = canvas.getContext('2d');
  const MAX_D  = 130;
  const MAX_D2 = MAX_D * MAX_D;
  let W, H, particles;

  function resize() {
    W = canvas.width  = canvas.offsetWidth;
    H = canvas.height = canvas.offsetHeight;
    const count = Math.max(20, Math.min(55, Math.floor((W * H) / 28000)));
    particles = Array.from({ length: count }, () => ({
      x:  Math.random() * W,
      y:  Math.random() * H,
      vx: (Math.random() - 0.5) * 0.22,
      vy: (Math.random() - 0.5) * 0.22,
      r:  Math.random() * 1.0 + 0.3,
    }));
  }

  function draw() {
    if (document.hidden) return;
    ctx.clearRect(0, 0, W, H);
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      p.x += p.vx; p.y += p.vy;
      if (p.x < 0 || p.x > W) p.vx *= -1;
      if (p.y < 0 || p.y > H) p.vy *= -1;
    }
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x;
        const dy = particles[i].y - particles[j].y;
        const d2 = dx * dx + dy * dy;
        if (d2 < MAX_D2) {
          ctx.beginPath();
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(particles[j].x, particles[j].y);
          ctx.strokeStyle = `rgba(255,255,255,${((1 - Math.sqrt(d2) / MAX_D) * 0.13).toFixed(3)})`;
          ctx.lineWidth   = 0.5;
          ctx.stroke();
        }
      }
    }
    ctx.fillStyle = 'rgba(255,255,255,0.42)';
    particles.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  resize();
  window.addEventListener('resize', resize, { passive: true });
  gsap.ticker.add(draw);
  gsap.from(canvas, { opacity: 0, duration: 2.0, ease: 'power2.inOut' });
}


/* ── Southern Cross: fade in + perpetual twinkle ── */
export function initSouthernCross() {
  const crux = document.getElementById('hero-crux');
  if (!crux) return;
  gsap.to(crux, { opacity: 1, duration: 1.6, delay: 0.5, ease: 'power2.inOut' });
  crux.querySelectorAll('.crux-star').forEach(star => {
    gsap.to(star, {
      opacity: 0.35 + Math.random() * 0.55,
      duration: 1.6 + Math.random() * 2.8,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
      delay: Math.random() * 3,
    });
  });
}


/* ── Topographic lines: draw in from left, parallax on scroll ── */
export function initTopoLines() {
  const lines = document.querySelectorAll('.topo-line, .topo-detail');
  if (!lines.length) return;

  lines.forEach(line => {
    const len = line.getTotalLength();
    gsap.set(line, { strokeDasharray: len, strokeDashoffset: len });
  });

  gsap.to(['.topo-line', '.topo-detail'], {
    strokeDashoffset: 0,
    duration: 2.6,
    stagger: { each: 0.09, from: 'start' },
    ease: 'power1.inOut',
  });

  // A ScrollTrigger block sat here, giving the hero parallax depth on scroll.
  // Removed 2026-08-15 along with the ScrollTrigger library itself: it fired on
  // `.hero--home` and moved `#hero-topo` and `#hero-crux`, none of which have
  // existed on any page since the SVG hero was replaced by the three.js
  // parallax hero. It had nothing to animate.
  //
  // The current hero drives its own scroll motion — hero-parallax.js reads
  // window.scrollY into a smoothed progress value on a passive listener. If
  // more scroll motion is wanted there, that is where it goes; a second scroll
  // system would fight the first.
  //
  // `git show 1d11277:src/assets/js/modules/hero-animations.js` has the
  // original if it is ever wanted back.
}


/* ── Navigation path: draws upward, fires waypoints, triggers flipboard ── */
export function initNavPath() {
  const path     = document.getElementById('hero-nav-path');
  const wpGroups = document.querySelectorAll('.waypoint-group');
  if (!path) return;

  // Precisely position each waypoint on the path using getPointAtLength
  const totalLen = path.getTotalLength();
  wpGroups.forEach(group => {
    const pt = path.getPointAtLength(parseFloat(group.dataset.t) * totalLen);
    group.querySelectorAll('circle').forEach(c => {
      c.setAttribute('cx', pt.x.toFixed(1));
      c.setAttribute('cy', pt.y.toFixed(1));
    });
  });

  gsap.set(path,     { strokeDasharray: totalLen, strokeDashoffset: totalLen });
  gsap.set(wpGroups, { opacity: 0 });

  const NAV_DURATION = 3.2;
  const NAV_DELAY    = 1.5;
  let   flipStarted  = false;
  const firedWPs     = new Set();

  gsap.to(path, {
    strokeDashoffset: 0,
    duration: NAV_DURATION,
    delay: NAV_DELAY,
    ease: 'power1.inOut',
    onUpdate() {
      const progress = this.progress();

      // Ephemeral waypoints: light up on arrival, fade before path leaves them
      wpGroups.forEach(group => {
        const t = parseFloat(group.dataset.t);
        if (progress >= t && !firedWPs.has(group)) {
          firedWPs.add(group);
          const ring = group.querySelector('.wp-ring');
          const core = group.querySelector('.wp-core');
          gsap.timeline()
            .to(core, { opacity: 1, scale: 1,   duration: 0.10, ease: 'power3.out' })
            .to(ring, { opacity: 1, scale: 2.0,  duration: 0.55, ease: 'power2.out' }, '<')
            .to(core, { opacity: 0, duration: 0.45, ease: 'power2.in' }, '+=0.12')
            .to(ring, { opacity: 0, duration: 0.38, ease: 'power2.in' }, '<');
        }
      });

      // Flipboard begins once path crosses the visual midpoint of the screen
      if (progress >= 0.48 && !flipStarted) {
        flipStarted = true;
        initHeroFlipboard();
      }
    },
  });
}


/* ── Split-flap headline reveal ── */
export function initHeroFlipboard() {
  const CHARS          = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!?/-•';
  const FLIPS          = 9;
  const FLIP_MS        = 52;
  const CHAR_STAGGER   = 40;

  const words    = document.querySelectorAll('.hero--home .hero-title .word');
  const subtitle = document.querySelector('.hero--home .hero-subtitle');
  const cta      = document.querySelector('.hero--home .hero-cta-wrapper');
  if (!words.length) return;

  // Phase 1: build all flip slots while words are still invisible
  let charIndex = 0;
  const slotQueue = [];

  words.forEach(word => {
    const span = word.querySelector('span');
    if (!span) return;
    const inner     = span.querySelector('em') || span;
    const finalText = inner.textContent;
    inner.innerHTML = '';

    [...finalText].forEach(ch => {
      const slot = document.createElement('span');
      slot.className = 'flip-slot';
      if (ch === ' ') { slot.innerHTML = '&nbsp;'; inner.appendChild(slot); return; }
      slot.textContent = CHARS[Math.floor(Math.random() * CHARS.length)];
      inner.appendChild(slot);
      slotQueue.push({ slot, finalChar: ch, index: charIndex++ });
    });
  });

  // Phase 2: reveal all words at once (scrambled content already in place)
  gsap.set(words, { opacity: 1 });

  // Phase 3: flip each slot to its final character in sequence
  slotQueue.forEach(({ slot, finalChar, index }) => {
    setTimeout(() => {
      let flips = 0;
      const doFlip = () => {
        const next = flips < FLIPS - 1
          ? CHARS[Math.floor(Math.random() * CHARS.length)]
          : finalChar;
        gsap.to(slot, {
          scaleY: 0,
          duration: 0.024,
          ease: 'power2.in',
          onComplete() {
            slot.textContent = next;
            gsap.to(slot, { scaleY: 1, duration: 0.024, ease: 'power2.out' });
          },
        });
        flips++;
        if (flips < FLIPS) setTimeout(doFlip, FLIP_MS);
      };
      doFlip();
    }, index * CHAR_STAGGER);
  });

  // Phase 4: subtitle and CTA after board settles
  const boardDoneMs = charIndex * CHAR_STAGGER + FLIPS * FLIP_MS + 180;
  if (subtitle) gsap.to(subtitle, { opacity: 1, y: 0, duration: 0.9,  delay: boardDoneMs / 1000,       ease: 'power2.out' });
  if (cta)      gsap.to(cta,      { opacity: 1, y: 0, duration: 0.7,  delay: boardDoneMs / 1000 + 0.2, ease: 'power2.out' });
}


/* =========================================
   5. SCROLL REVEALS
   Uses IntersectionObserver — no ScrollTrigger ticker overhead.
   ========================================= */
export function initScrollReveals() {
  if (!('IntersectionObserver' in window)) return;

  const io = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      gsap.from(entry.target, { y: 40, opacity: 0, duration: 1, ease: 'power3.out' });
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.15 });

  const ioFade = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      gsap.from(entry.target, { opacity: 0, duration: 1.5 });
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.section-header').forEach(el => io.observe(el));
  document.querySelectorAll('.logo-section .logo-track, .membership-section .logo-track').forEach(el => ioFade.observe(el));
}


